/**
 * Rich replies (ADR-0088): what an AI step shows besides its text answer — quick replies,
 * buttons, cards, a table, a form and links — as the builder designs it (`node.reply`, the SDK's
 * `ReplyDesign`) and as a run streams it (CUSTOM `rich_reply`, already filled with the run's
 * values). Pure helpers shared by the chat renderers, the Reply designer and the mocks:
 * parsing, a local preview of the filling, the channel text versions, the link allow-list and
 * the designer's data checks. Never HTML: a fixed block schema.
 */
import type { AgentSpec, SpecNode } from "./spec/types";
import type { Text } from "./types";

// ---------------------------------------------------------------------------- design (spec)

export type ReplyText = { en: string; ar: string };
export type ReplyActionDesign =
  | { type: "reply"; value?: string | null }
  | { type: "link"; url: string }
  | { type: "action"; step: string }
  /** Continue elsewhere and come back (ADR-0094): opens `url` with a one-time return link; the run
   * waits at the `wait` step ("Wait for") until the page sends the person back. */
  | { type: "handoff"; url: string; wait: string; return_param?: string };
export interface ReplyButtonDesign {
  id: string;
  label: ReplyText;
  action: ReplyActionDesign;
}
export type ReplyFieldType = "text" | "number" | "date" | "choice";
export interface ReplyFormField {
  key: string;
  label: ReplyText;
  type: ReplyFieldType;
  required?: boolean;
  options?: string[];
  min?: number | null;
  max?: number | null;
  max_length?: number | null;
}
export type ReplyBlockDesign =
  | { kind: "text"; id: string; text: ReplyText }
  | { kind: "quick_replies"; id: string; options: ReplyButtonDesign[]; ai_pick?: number | null }
  | { kind: "buttons"; id: string; buttons: ReplyButtonDesign[] }
  | { kind: "form"; id: string; title?: ReplyText | null; fields: ReplyFormField[]; submit?: ReplyText | null }
  | { kind: "cards"; id: string; items: string; max?: number; title: ReplyText; subtitle?: ReplyText | null; image?: string | null; fields?: { label: ReplyText; value: string }[]; buttons?: ReplyButtonDesign[] }
  | { kind: "table"; id: string; items: string; max?: number; columns: { label: ReplyText; value: string }[] }
  | { kind: "link"; id: string; label: ReplyText; url: string };
export type ReplyBlockKind = ReplyBlockDesign["kind"];
export interface ReplyDesign {
  blocks: ReplyBlockDesign[];
}
export const REPLY_BLOCK_KINDS: readonly ReplyBlockKind[] = ["text", "quick_replies", "buttons", "cards", "table", "form", "link"];
export const REPLY_FIELD_TYPES: readonly ReplyFieldType[] = ["text", "number", "date", "choice"];

// ---------------------------------------------------------------------------- run (rendered)

export interface RichAction {
  type: "reply" | "link" | "action" | (string & {});
  value?: string;
  url?: string;
  /** Where a link goes (shown as "Opens <host>"); none for console links. */
  host?: string | null;
  step?: string;
  step_label?: Text | null;
  /** Continue elsewhere (ADR-0094): the step the run waits at. */
  wait?: string;
}
export interface RichButton {
  id: string;
  label: Text;
  action: RichAction;
}
export interface RichCard {
  index: number;
  title: Text;
  subtitle?: Text | null;
  image?: string | null;
  fields: { label: Text | null; value: string }[];
  buttons: RichButton[];
}
export interface RichField {
  key: string;
  label: Text;
  type: ReplyFieldType;
  required: boolean;
  options: string[];
  min?: number;
  max?: number;
  max_length?: number;
}
export type RichBlock =
  | { kind: "text"; id: string; text: Text }
  | { kind: "quick_replies"; id: string; options: RichButton[] }
  | { kind: "buttons"; id: string; buttons: RichButton[] }
  | { kind: "cards"; id: string; cards: RichCard[] }
  | { kind: "table"; id: string; columns: Text[]; rows: string[][] }
  | { kind: "link"; id: string; label: Text; url: string; host?: string | null }
  | { kind: "form"; id: string; title: Text | null; submit: Text; fields: RichField[] };
export interface RichReply {
  reply_id: string;
  node: string | null;
  lang: "ar" | "en";
  /** The run waits for a click (a button runs a step). */
  waiting: boolean;
  blocks: RichBlock[];
  fallback: Text;
  /** The plain-text message this reply replaces in the chat. */
  message_id?: string | null;
  /** Clicked (here, or on another screen) or no longer answerable. */
  used?: boolean;
  /** The run that made it (clicks go to this run). */
  run_id?: string | null;
}

/** What a click sends to `POST /v1/runs/{run}/replies/{reply}/clicks`. */
export interface ReplyClickIn {
  button?: string;
  card?: number;
  form?: Record<string, string>;
}
export interface ReplyClickOut {
  outcome: "resumed" | "started" | "duplicate" | "message";
  run_id?: string | null;
  text?: string | null;
  button?: string | null;
  /** Continue elsewhere (ADR-0094): the page to open, with its one-time return link. */
  open_url?: string | null;
}

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const text = (v: unknown): Text | null => (isObj(v) && (typeof v.en === "string" || typeof v.ar === "string") ? { en: String(v.en ?? v.ar ?? ""), ar: String(v.ar ?? v.en ?? "") } : null);
const str = (v: unknown): string => (typeof v === "string" ? v : "");

function button(v: unknown): RichButton | null {
  if (!isObj(v) || typeof v.id !== "string") return null;
  const a = isObj(v.action) ? v.action : {};
  const action: RichAction = { type: str(a.type) || "reply" };
  if (typeof a.value === "string") action.value = a.value;
  if (typeof a.url === "string") action.url = a.url;
  if (typeof a.host === "string") action.host = a.host;
  if (typeof a.step === "string") action.step = a.step;
  if (typeof a.wait === "string") action.wait = a.wait;
  const sl = text(a.step_label);
  if (sl) action.step_label = sl;
  return { id: v.id, label: text(v.label) ?? { en: v.id, ar: v.id }, action };
}
const buttons = (v: unknown): RichButton[] => (Array.isArray(v) ? v.map(button).filter((b): b is RichButton => !!b) : []);

/** The reply carried by a CUSTOM `rich_reply` event (defensive: unknown blocks are dropped). */
export function richReplyOf(e: Record<string, unknown>): RichReply | null {
  const inner = isObj(e.value) && typeof e.value.reply_id === "string" ? e.value : e;
  if (typeof inner.reply_id !== "string" || !Array.isArray(inner.blocks)) return null;
  const blocks: RichBlock[] = [];
  for (const b of inner.blocks as unknown[]) {
    if (!isObj(b) || typeof b.id !== "string") continue;
    const id = b.id;
    switch (b.kind) {
      case "text": {
        const t = text(b.text);
        if (t) blocks.push({ kind: "text", id, text: t });
        break;
      }
      case "quick_replies":
        blocks.push({ kind: "quick_replies", id, options: buttons(b.options) });
        break;
      case "buttons":
        blocks.push({ kind: "buttons", id, buttons: buttons(b.buttons) });
        break;
      case "cards":
        blocks.push({
          kind: "cards",
          id,
          cards: (Array.isArray(b.cards) ? b.cards : []).filter(isObj).map((c, i) => ({
            index: typeof c.index === "number" ? c.index : i,
            title: text(c.title) ?? { en: "", ar: "" },
            subtitle: text(c.subtitle),
            image: typeof c.image === "string" ? c.image : null,
            fields: (Array.isArray(c.fields) ? c.fields : []).filter(isObj).map((f) => ({ label: text(f.label), value: str(f.value) })),
            buttons: buttons(c.buttons),
          })),
        });
        break;
      case "table":
        blocks.push({
          kind: "table",
          id,
          columns: (Array.isArray(b.columns) ? b.columns : []).map((c) => text(c) ?? { en: "", ar: "" }),
          rows: (Array.isArray(b.rows) ? b.rows : []).filter(Array.isArray).map((r) => (r as unknown[]).map((x) => String(x ?? ""))),
        });
        break;
      case "link":
        if (typeof b.url === "string") blocks.push({ kind: "link", id, label: text(b.label) ?? { en: b.url, ar: b.url }, url: b.url, host: typeof b.host === "string" ? b.host : null });
        break;
      case "form":
        blocks.push({
          kind: "form",
          id,
          title: text(b.title),
          submit: text(b.submit) ?? { en: "Send", ar: "إرسال" },
          fields: (Array.isArray(b.fields) ? b.fields : []).filter(isObj).map((f) => {
            const field: RichField = { key: str(f.key), label: text(f.label) ?? { en: str(f.key), ar: str(f.key) }, type: (REPLY_FIELD_TYPES as readonly string[]).includes(str(f.type)) ? (f.type as ReplyFieldType) : "text", required: f.required === true, options: Array.isArray(f.options) ? f.options.map(String) : [] };
            if (typeof f.min === "number") field.min = f.min;
            if (typeof f.max === "number") field.max = f.max;
            if (typeof f.max_length === "number") field.max_length = f.max_length;
            return field;
          }),
        });
        break;
      default:
        break;
    }
  }
  return {
    reply_id: inner.reply_id,
    node: typeof inner.node === "string" ? inner.node : null,
    lang: inner.lang === "ar" ? "ar" : "en",
    waiting: inner.waiting === true,
    blocks,
    fallback: text(inner.fallback) ?? { en: "", ar: "" },
    message_id: typeof inner.message_id === "string" ? inner.message_id : null,
    used: inner.used === true,
    run_id: typeof e.runId === "string" ? e.runId : typeof inner.run_id === "string" ? inner.run_id : null,
  };
}

export function richText(t: Text | null | undefined, lang: "ar" | "en"): string {
  if (!t) return "";
  return t[lang] || t.en || t.ar || "";
}

// ---------------------------------------------------------------------------- binding (preview)

const SEG = /[A-Za-z0-9_]+|\[\d+\]/g;
/** A value path as reply placeholders write it: key, then .field or [n]. */
export const REPLY_PATH = /^[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z0-9_]+|\[\d+\])*$/;
const PLACEHOLDER = /(?<!\{)\{([A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z0-9_]+|\[\d+\])*)\}(?!\})/g;

/** The placeholders of a template, in order (`{city}`, `{search.clubs[0].name}`). */
export function replyPlaceholders(template: string | null | undefined): string[] {
  return [...(template ?? "").matchAll(PLACEHOLDER)].map((m) => m[1] as string);
}

export function resolvePath(path: string, data: unknown): unknown {
  let cur: unknown = data;
  for (const tok of path.match(SEG) ?? []) {
    if (cur === null || cur === undefined) return undefined;
    if (tok.startsWith("[")) {
      const i = Number(tok.slice(1, -1));
      cur = Array.isArray(cur) ? cur[i] : undefined;
    } else if (Array.isArray(cur) && /^\d+$/.test(tok)) cur = cur[Number(tok)];
    else cur = isObj(cur) ? cur[tok] : undefined;
  }
  return cur;
}

export function valueText(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (Array.isArray(v) && v.every((x) => !isObj(x) && !Array.isArray(x))) return v.map(valueText).join(", ");
  return JSON.stringify(v);
}

function lookup(path: string, data: unknown, item: unknown): unknown {
  if (item !== undefined) {
    if (path === "item") return item;
    if (path.startsWith("item.") || path.startsWith("item[")) return resolvePath(`x${path.slice(4)}`, { x: item });
    const root = /^[A-Za-z0-9_]+/.exec(path)?.[0];
    if (isObj(item) && root && root in item) return resolvePath(path, item);
  }
  return resolvePath(path, data);
}

/** A template filled with values (`url`: values URL-encoded unless the placeholder is the whole link). */
export function fillReplyTemplate(template: string, data: unknown, item?: unknown, url = false): string {
  const whole = /^\s*\{([A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z0-9_]+|\[\d+\])*)\}\s*$/.exec(template);
  if (whole) return valueText(lookup(whole[1] as string, data, item)).trim();
  return template.replace(PLACEHOLDER, (_m, p: string) => {
    const v = valueText(lookup(p, data, item));
    return url ? encodeURIComponent(v) : v;
  });
}

// ---------------------------------------------------------------------------- links

export interface LinkRules {
  /** Allowed web hosts (`example.com`, `*.example.com`). */
  domains: readonly string[];
  /** Allowed app schemes (`myapp`). */
  schemes: readonly string[];
}
export const NO_LINKS: LinkRules = { domains: [], schemes: [] };

export function hostOf(url: string): string | null {
  const u = url.trim();
  if (u.startsWith("/") && !u.startsWith("//")) return null;
  const m = /^([A-Za-z][A-Za-z0-9+.-]*):(\/\/([^/?#:@]*@)?([^/?#:]*))?/.exec(u);
  if (!m) return null;
  return (m[4] || "").toLowerCase() || `${(m[1] as string).toLowerCase()}:`;
}

/** Whether a link may be shown: console paths always; web hosts and app schemes on the allow-list. */
export function linkAllowed(url: string, rules: LinkRules): boolean {
  const u = url.trim();
  if (!u) return false;
  if (u.startsWith("/") && !u.startsWith("//")) return true;
  const scheme = /^([A-Za-z][A-Za-z0-9+.-]*):/.exec(u)?.[1]?.toLowerCase();
  if (!scheme) return false;
  if (scheme === "http" || scheme === "https") {
    const host = hostOf(u);
    return !!host && rules.domains.some((d) => host === d || (d.startsWith("*.") && host.endsWith(d.slice(1))));
  }
  return rules.schemes.includes(scheme);
}

/** A link template the checks can judge now: it starts with a fixed address, not a value. */
export function fixedLink(url: string): boolean {
  return !/^\s*\{/.test(url);
}

// ---------------------------------------------------------------------------- preview render

const T = (en: string, ar: string): Text => ({ en, ar });
const SEND = T("Send", "إرسال");
const WORDS = {
  en: { choose: "Reply with the number or the words of your choice:", fill: "Please tell us:", required: "required" },
  ar: { choose: "ردّ برقم اختيارك أو بكلماته:", fill: "يرجى إخبارنا بما يلي:", required: "مطلوب" },
};

function filled(t: ReplyText | null | undefined, data: unknown, item?: unknown): Text | null {
  if (!t) return null;
  const pickOne = (l: "ar" | "en") => (t[l]?.trim() ? t[l] : t[l === "ar" ? "en" : "ar"]) ?? "";
  const out = { en: fillReplyTemplate(pickOne("en"), data, item), ar: fillReplyTemplate(pickOne("ar"), data, item) };
  return out.en.trim() || out.ar.trim() ? out : null;
}

function previewButton(b: ReplyButtonDesign, data: unknown, item: unknown, spec: AgentSpec | null): RichButton {
  const label = filled(b.label, data, item) ?? { en: b.id, ar: b.id };
  const a = b.action;
  if (a.type === "reply") return { id: b.id, label, action: { type: "reply", value: a.value ? fillReplyTemplate(a.value, data, item) : label.en || label.ar } };
  if (a.type === "link") return { id: b.id, label, action: { type: "link", url: fillReplyTemplate(a.url, data, item, true) } };
  if (a.type === "action") {
    const step = spec?.spec.graph.nodes.find((n) => n.id === a.step);
    return { id: b.id, label, action: { type: "action", step: a.step, step_label: step?.label ? { en: step.label.en || step.label.ar, ar: step.label.ar || step.label.en } : null } };
  }
  return { id: b.id, label, action: { type: "handoff", url: fillReplyTemplate(a.url, data, item, true), wait: a.wait } };
}

function items(path: string, data: unknown, cap: number): unknown[] {
  const v = resolvePath(path, data);
  return Array.isArray(v) ? v.slice(0, cap) : [];
}

/** The reply as a run would show it with these values (the designer's live preview). */
export function previewReply(design: ReplyDesign, data: unknown, opts: { lang?: "ar" | "en"; rules?: LinkRules; spec?: AgentSpec | null; replyId?: string } = {}): RichReply {
  const spec = opts.spec ?? null;
  const blocks: RichBlock[] = [];
  for (const b of design.blocks) {
    if (b.kind === "text") {
      const t = filled(b.text, data);
      if (t) blocks.push({ kind: "text", id: b.id, text: t });
    } else if (b.kind === "quick_replies") {
      blocks.push({ kind: "quick_replies", id: b.id, options: b.options.map((o) => previewButton(o, data, undefined, spec)) });
    } else if (b.kind === "buttons") {
      blocks.push({ kind: "buttons", id: b.id, buttons: b.buttons.map((o) => previewButton(o, data, undefined, spec)) });
    } else if (b.kind === "form") {
      blocks.push({
        kind: "form",
        id: b.id,
        title: filled(b.title, data),
        submit: filled(b.submit, data) ?? SEND,
        fields: b.fields.map((f) => {
          const out: RichField = { key: f.key, label: filled(f.label, data) ?? { en: f.key, ar: f.key }, type: f.type, required: !!f.required, options: f.options ?? [] };
          if (typeof f.min === "number") out.min = f.min;
          if (typeof f.max === "number") out.max = f.max;
          if (typeof f.max_length === "number") out.max_length = f.max_length;
          return out;
        }),
      });
    } else if (b.kind === "cards") {
      blocks.push({
        kind: "cards",
        id: b.id,
        cards: items(b.items, data, b.max ?? 10).map((it, index) => ({
          index,
          title: filled(b.title, data, it) ?? { en: String(index + 1), ar: String(index + 1) },
          subtitle: filled(b.subtitle, data, it),
          image: b.image ? fillReplyTemplate(b.image, data, it, true) || null : null,
          fields: (b.fields ?? []).map((f) => ({ label: filled(f.label, data, it), value: fillReplyTemplate(f.value, data, it) })).filter((f) => f.value.trim()),
          buttons: (b.buttons ?? []).map((x) => previewButton(x, data, it, spec)),
        })),
      });
    } else if (b.kind === "table") {
      blocks.push({ kind: "table", id: b.id, columns: b.columns.map((c) => filled(c.label, data) ?? T("", "")), rows: items(b.items, data, b.max ?? 20).map((it) => b.columns.map((c) => fillReplyTemplate(c.value, data, it))) });
    } else if (b.kind === "link") {
      blocks.push({ kind: "link", id: b.id, label: filled(b.label, data) ?? T(b.url, b.url), url: fillReplyTemplate(b.url, data, undefined, true) });
    }
  }
  const reply: RichReply = { reply_id: opts.replyId ?? "preview", node: null, lang: opts.lang ?? "en", waiting: design.blocks.some((b) => buttonsOf(b).some((x) => x.action.type === "action" || x.action.type === "handoff")), blocks, fallback: T("", "") };
  const kept = applyLinkRules(reply, opts.rules ?? NO_LINKS);
  kept.fallback = { en: fallbackText(kept, "en"), ar: fallbackText(kept, "ar") };
  return kept;
}

/** The reply without links the allow-list does not allow; kept links carry their host. */
export function applyLinkRules(reply: RichReply, rules: LinkRules): RichReply {
  const keep = (b: RichButton): boolean => {
    if (b.action.type === "unavailable") return false;
    if (b.action.type !== "link" && b.action.type !== "handoff") return true;
    if (!linkAllowed(b.action.url ?? "", rules)) return false;
    b.action.host = hostOf(b.action.url ?? "");
    return true;
  };
  const blocks: RichBlock[] = [];
  for (const raw of reply.blocks) {
    const b = JSON.parse(JSON.stringify(raw)) as RichBlock;
    if (b.kind === "link") {
      if (!linkAllowed(b.url, rules)) continue;
      b.host = hostOf(b.url);
    } else if (b.kind === "buttons") {
      b.buttons = b.buttons.filter(keep);
      if (!b.buttons.length) continue;
    } else if (b.kind === "quick_replies") b.options = b.options.filter(keep);
    else if (b.kind === "cards") {
      for (const c of b.cards) {
        c.buttons = c.buttons.filter(keep);
        if (c.image && !linkAllowed(c.image, rules)) c.image = null;
      }
    }
    blocks.push(b);
  }
  return { ...reply, blocks };
}

function buttonsOf(b: ReplyBlockDesign): ReplyButtonDesign[] {
  if (b.kind === "quick_replies") return b.options;
  if (b.kind === "buttons") return b.buttons;
  if (b.kind === "cards") return b.buttons ?? [];
  return [];
}

/** Every clickable choice of a rendered reply, as text channels number them. */
export function numberedChoices(reply: RichReply): { button: RichButton; card: RichCard | null }[] {
  const out: { button: RichButton; card: RichCard | null }[] = [];
  for (const b of reply.blocks) {
    if (b.kind === "quick_replies") out.push(...b.options.map((o) => ({ button: o, card: null })));
    else if (b.kind === "buttons") out.push(...b.buttons.map((o) => ({ button: o, card: null })));
    else if (b.kind === "cards") for (const c of b.cards) out.push(...c.buttons.map((o) => ({ button: o, card: c })));
  }
  return out.filter((c) => c.button.action.type === "reply" || c.button.action.type === "action" || c.button.action.type === "handoff");
}

/** The plain-text version kept in the conversation history (as the orchestrator writes it). */
export function fallbackText(reply: RichReply, lang: "ar" | "en"): string {
  const w = WORDS[lang];
  const lines: string[] = [];
  for (const b of reply.blocks) {
    if (b.kind === "text") lines.push(richText(b.text, lang));
    else if (b.kind === "cards")
      for (const c of b.cards) {
        const sub = richText(c.subtitle, lang);
        lines.push(`• ${richText(c.title, lang)}${sub ? ` — ${sub}` : ""}`);
        for (const f of c.fields) lines.push(`  ${richText(f.label, lang)}: ${f.value}`);
      }
    else if (b.kind === "table") {
      const cols = b.columns.map((c) => richText(c, lang));
      if (cols.some(Boolean)) lines.push(cols.join(" | "));
      for (const r of b.rows) lines.push(r.join(" | "));
    } else if (b.kind === "link") lines.push(`${richText(b.label, lang)}: ${b.url}`);
    else if (b.kind === "form") {
      lines.push(richText(b.title, lang) || w.fill);
      for (const f of b.fields) lines.push(`- ${richText(f.label, lang)}${f.required ? ` (${w.required})` : ""}`);
    }
  }
  const choices = numberedChoices(reply);
  if (choices.length) {
    lines.push(w.choose);
    choices.forEach((c, i) => lines.push(`${i + 1}. ${richText(c.button.label, lang)}${c.card ? ` (${richText(c.card.title, lang)})` : ""}`));
  }
  return lines.filter((l) => l.trim()).join("\n");
}

// ---------------------------------------------------------------------------- channel versions

export type FallbackChannel = "website" | "text" | "voice";
const CH = {
  en: { replyWith: "Reply {n}, or with the words of your choice.", or: "or", opens: "opens {host}", form: "I'll ask you {count} short questions, one at a time.", say: "Say {number} for {label}.", link: "There is also a link to {host}; I can send it in writing.", voiceForm: "I have {count} short questions for you." },
  ar: { replyWith: "ردّ بالرقم {n}، أو بكلمات اختيارك.", or: "أو", opens: "يفتح {host}", form: "سأطرح عليك {count} أسئلة قصيرة، سؤالاً تلو الآخر.", say: "قل {number} لـ {label}.", link: "يوجد أيضاً رابط إلى {host}؛ يمكنني إرساله كتابةً.", voiceForm: "لديّ {count} أسئلة قصيرة لك." },
};
const SPOKEN = { en: ["one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"], ar: ["واحد", "اثنين", "ثلاثة", "أربعة", "خمسة", "ستة", "سبعة", "ثمانية", "تسعة", "عشرة"] };
const fmt = (s: string, v: Record<string, string | number>) => s.replace(/\{(\w+)\}/g, (_m, k: string) => String(v[k] ?? ""));

function links(reply: RichReply): { label: Text; url: string; host: string | null }[] {
  const out: { label: Text; url: string; host: string | null }[] = [];
  for (const b of reply.blocks) {
    if (b.kind === "link") out.push({ label: b.label, url: b.url, host: b.host ?? null });
    const bs = b.kind === "buttons" ? b.buttons : b.kind === "cards" ? b.cards.flatMap((c) => c.buttons) : [];
    for (const x of bs) if (x.action.type === "link" && x.action.url) out.push({ label: x.label, url: x.action.url, host: x.action.host ?? null });
  }
  return out;
}

/**
 * What a channel shows, as the channels service makes it: the reply's own text on the website
 * (which also draws it), numbered choices on WhatsApp, SMS, e-mail and chat apps, and the
 * choices read aloud on voice.
 */
export function channelText(reply: RichReply, channel: FallbackChannel, lang: "ar" | "en", answer = ""): string {
  const w = CH[lang];
  if (channel === "website") return [answer, fallbackText(reply, lang)].filter((x) => x.trim()).join("\n\n");
  const choices = numberedChoices(reply);
  const form = reply.blocks.find((b) => b.kind === "form");
  if (channel === "voice") {
    const parts: string[] = answer.trim() ? [answer.trim()] : [];
    for (const b of reply.blocks) {
      if (b.kind === "text") parts.push(richText(b.text, lang));
      else if (b.kind === "cards") parts.push(b.cards.slice(0, 5).map((c) => richText(c.title, lang)).join(lang === "ar" ? "، " : ", "));
    }
    choices.slice(0, 10).forEach((c, i) => parts.push(fmt(w.say, { number: SPOKEN[lang][i] ?? String(i + 1), label: `${richText(c.button.label, lang)} ${c.card ? richText(c.card.title, lang) : ""}`.trim() })));
    const first = links(reply).find((l) => l.host);
    if (first) parts.push(fmt(w.link, { host: first.host ?? "" }));
    if (form && form.kind === "form") parts.push(fmt(w.voiceForm, { count: form.fields.length }));
    return parts.filter((p) => p.trim()).join(" ");
  }
  const lines: string[] = answer.trim() ? [answer.trim(), ""] : [];
  for (const b of reply.blocks) {
    if (b.kind === "text") lines.push(richText(b.text, lang));
    else if (b.kind === "cards")
      for (const c of b.cards) {
        const sub = richText(c.subtitle, lang);
        lines.push(`• ${richText(c.title, lang)}${sub ? ` — ${sub}` : ""}`);
        for (const f of c.fields) lines.push(`  ${richText(f.label, lang)}: ${f.value}`);
      }
    else if (b.kind === "table") {
      const cols = b.columns.map((c) => richText(c, lang));
      for (const r of b.rows) lines.push(r.map((v, i) => (cols[i] ? `${cols[i]}: ${v}` : v)).join(" · "));
    }
  }
  for (const l of links(reply)) lines.push(`${richText(l.label, lang)}${l.host ? ` (${fmt(w.opens, { host: l.host })})` : ""}: ${l.url}`);
  if (choices.length) {
    choices.forEach((c, i) => lines.push(`${i + 1}. ${richText(c.button.label, lang)}${c.card ? ` — ${richText(c.card.title, lang)}` : ""}`));
    const nums = choices.map((_c, i) => String(i + 1));
    const n = nums.length === 1 ? nums[0] : `${nums.slice(0, -1).join(lang === "ar" ? "، " : ", ")} ${w.or} ${nums[nums.length - 1]}`;
    lines.push(fmt(w.replyWith, { n: n ?? "1" }));
  }
  if (form && form.kind === "form") {
    if (form.title) lines.push(richText(form.title, lang));
    lines.push(fmt(w.form, { count: form.fields.length }));
  }
  return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

// ---------------------------------------------------------------------------- designer

/** The reply design of an AI step, when it has one. */
export function replyOf(node: Pick<SpecNode, "reply"> | null | undefined): ReplyDesign | null {
  const r = node?.reply as unknown;
  return isObj(r) && Array.isArray(r.blocks) ? (r as unknown as ReplyDesign) : null;
}

/** A block or button id not used yet in the reply (`more`, `more_2`). */
export function freeReplyId(design: ReplyDesign | null, base: string): string {
  const taken = new Set<string>();
  for (const b of design?.blocks ?? []) {
    taken.add(b.id);
    for (const x of buttonsOf(b)) taken.add(x.id);
  }
  const clean = base.toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^[^a-z]+/, "").replace(/_+$/, "").slice(0, 30) || "item";
  if (!taken.has(clean)) return clean;
  for (let i = 2; ; i++) if (!taken.has(`${clean}_${i}`)) return `${clean}_${i}`;
}

/** A new block of a kind, with sensible first content the builder then edits. */
export function newReplyBlock(kind: ReplyBlockKind, design: ReplyDesign | null): ReplyBlockDesign {
  const id = freeReplyId(design, kind === "quick_replies" ? "choices" : kind);
  const yes = { id: freeReplyId(design, "yes"), label: T("Yes", "نعم"), action: { type: "reply" as const } };
  switch (kind) {
    case "text":
      return { kind, id, text: T("", "") };
    case "quick_replies":
      return { kind, id, options: [yes, { id: freeReplyId({ blocks: [...(design?.blocks ?? []), { kind: "buttons", id, buttons: [yes] }] }, "no"), label: T("No", "لا"), action: { type: "reply" } }] };
    case "buttons":
      return { kind, id, buttons: [{ ...yes, label: T("Continue", "متابعة") }] };
    case "form":
      return { kind, id, title: null, fields: [{ key: "name", label: T("Name", "الاسم"), type: "text", required: true }] };
    case "cards":
      return { kind, id, items: "", max: 10, title: T("", ""), buttons: [] };
    case "table":
      return { kind, id, items: "", max: 20, columns: [{ label: T("", ""), value: "" }] };
    case "link":
      return { kind, id, label: T("", ""), url: "https://" };
  }
}

/** The design with a block moved by `delta` places (keyboard reorder). */
export function moveReplyBlock(design: ReplyDesign, index: number, delta: number): ReplyDesign {
  const to = index + delta;
  if (to < 0 || to >= design.blocks.length) return design;
  const blocks = design.blocks.slice();
  const [b] = blocks.splice(index, 1);
  blocks.splice(to, 0, b as ReplyBlockDesign);
  return { blocks };
}

/** "Use a system" steps of the agent a button can run. */
export function actionTargets(spec: AgentSpec, nodeId: string): SpecNode[] {
  return spec.spec.graph.nodes.filter((n) => n.type === "tool" && n.id !== nodeId);
}

/**
 * The spec with an AI step's reply set, and an arrow from the step to every system step one of
 * its buttons runs (the arrow a click follows; added once, never removed here).
 */
export function withReply(spec: AgentSpec, nodeId: string, design: ReplyDesign | null): AgentSpec {
  const nodes = spec.spec.graph.nodes.map((n) => {
    if (n.id !== nodeId) return n;
    if (!design || !design.blocks.length) {
      const { reply: _r, ...rest } = n;
      return rest as SpecNode;
    }
    return { ...n, reply: design };
  });
  const edges = [...(spec.spec.graph.edges ?? [])];
  for (const b of design?.blocks ?? [])
    for (const x of buttonsOf(b))
      if (x.action.type === "action" && x.action.step && !edges.some((e) => e.from === nodeId && e.to === (x.action as { step: string }).step)) {
        edges.push({ from: nodeId, to: x.action.step, label: { en: x.label.en || x.label.ar, ar: x.label.ar || x.label.en } });
      } else if (x.action.type === "handoff" && x.action.wait && !edges.some((e) => e.from === nodeId && e.to === (x.action as { wait: string }).wait)) {
        // ADR-0094: the run goes on to the wait step only by this button.
        edges.push({ from: nodeId, to: x.action.wait, label: { en: x.label.en || x.label.ar, ar: x.label.ar || x.label.en } });
      }
  return { ...spec, spec: { ...spec.spec, graph: { ...spec.spec.graph, nodes, edges } } };
}

// ---------------------------------------------------------------------------- checks

export type ReplyIssueCode = "dead_button" | "missing_step" | "not_list" | "unknown_list" | "link_not_allowed" | "missing_wait" | "empty_text";
export interface ReplyIssue {
  code: ReplyIssueCode;
  severity: "error" | "warning";
  block: string;
  button?: string;
  /** Plain values for the message: a button's label, a value path, a host. */
  values: Record<string, string>;
}

/** The kind of a value at a path (`array`, `object`, …), or null when unknown. */
export type SchemaTypeOf = (path: string) => readonly string[] | null;

/**
 * The Reply designer's data checks: a button that leads nowhere, an action whose step is
 * missing, a card list or table bound to a non-list or unknown value, a link whose host is not
 * on the organisation's allow-list.
 */
export function replyIssues(spec: AgentSpec, nodeId: string, design: ReplyDesign, opts: { rules?: LinkRules | null; typeOf?: SchemaTypeOf; known?: (root: string) => boolean } = {}): ReplyIssue[] {
  const out: ReplyIssue[] = [];
  const tools = new Set(spec.spec.graph.nodes.filter((n) => n.type === "tool").map((n) => n.id));
  const waits = new Set(spec.spec.graph.nodes.filter((n) => n.type === "wait").map((n) => n.id));
  const label = (b: ReplyButtonDesign) => b.label.en || b.label.ar || b.id;
  const link = (url: string, block: string, button?: string, name = "") => {
    if (!url.trim() || url.trim() === "https://" || (fixedLink(url) && !/^([A-Za-z][A-Za-z0-9+.-]*:|\/(?!\/))/.test(url.trim()))) {
      out.push({ code: "dead_button", severity: "error", block, ...(button ? { button } : {}), values: { label: name || url } });
      return;
    }
    if (opts.rules && fixedLink(url) && !linkAllowed(url.replace(/\{[^}]*\}/g, "x"), opts.rules)) out.push({ code: "link_not_allowed", severity: "error", block, ...(button ? { button } : {}), values: { host: hostOf(url) ?? url, label: name } });
  };
  for (const b of design.blocks) {
    if (b.kind === "text" && !b.text.en.trim() && !b.text.ar.trim()) out.push({ code: "empty_text", severity: "error", block: b.id, values: {} });
    if (b.kind === "link") link(b.url, b.id, undefined, b.label.en || b.label.ar);
    for (const x of buttonsOf(b)) {
      const a = x.action;
      if (a.type === "reply" && a.value === "") out.push({ code: "dead_button", severity: "error", block: b.id, button: x.id, values: { label: label(x) } });
      else if (a.type === "action" && (!a.step || !tools.has(a.step))) out.push({ code: a.step ? "missing_step" : "dead_button", severity: "error", block: b.id, button: x.id, values: { label: label(x) } });
      else if (a.type === "link") link(a.url, b.id, x.id, label(x));
      else if (a.type === "handoff") {
        link(a.url ?? "", b.id, x.id, label(x));
        if (!a.wait || !waits.has(a.wait)) out.push({ code: "missing_wait", severity: "error", block: b.id, button: x.id, values: { label: label(x) } });
      }
    }
    if (b.kind === "cards" || b.kind === "table") {
      const root = /^[A-Za-z_][A-Za-z0-9_]*/.exec(b.items)?.[0];
      if (!b.items || !REPLY_PATH.test(b.items) || (root && opts.known && !opts.known(root))) out.push({ code: "unknown_list", severity: "error", block: b.id, values: { path: b.items } });
      else {
        const types = opts.typeOf?.(b.items) ?? null;
        if (types && types.length && !types.includes("array")) out.push({ code: "not_list", severity: "error", block: b.id, values: { path: b.items } });
      }
    }
  }
  void nodeId;
  return out;
}
